import { TovChunkListResponseSchema } from "contracts/api/client-tov/evidence.responses";
import { TovMapEnqueueResponseSchema } from "contracts/api/client-tov/mapping.responses";
import { TovMapAttemptSummaryResponseSchema } from "contracts/api/client-tov/mapping-attempts";
import type {
  TovProfileCreateRequest,
  TovTraitCreateRequest,
  TovVersionCreateRequest,
} from "contracts/api/client-tov/requests";
import {
  TovProfileListResponseSchema,
  TovProfileResponseSchema,
} from "contracts/api/client-tov/responses";
import type { TovSourceUploadMeta } from "contracts/api/client-tov/sources.requests";
import {
  TovParsedPageListResponseSchema,
  TovSourceListResponseSchema,
  TovSourceResponseSchema,
} from "contracts/api/client-tov/sources.responses";
import {
  TovVersionListResponseSchema,
  TovVersionResponseSchema,
} from "contracts/api/client-tov/version.responses";
import { request, uploadRequest } from "./api-client";

export const tovApi = {
  listProfiles: (clientId: string) =>
    request(
      "GET",
      `/api/clients/${clientId}/tov/profiles`,
      TovProfileListResponseSchema,
    ),
  createProfile: (clientId: string, input: TovProfileCreateRequest) =>
    request(
      "POST",
      `/api/clients/${clientId}/tov/profiles`,
      TovProfileResponseSchema,
      input,
    ),
  getVersion: (clientId: string, versionId: string) =>
    request(
      "GET",
      `/api/clients/${clientId}/tov/versions/${versionId}`,
      TovVersionResponseSchema,
    ),
  listVersions: (clientId: string, profileId: string) =>
    request(
      "GET",
      `/api/clients/${clientId}/tov/profiles/${profileId}/versions`,
      TovVersionListResponseSchema,
    ),
  updateDefinition: (
    clientId: string,
    path: string,
    id: string,
    input: unknown,
  ) =>
    request(
      "PUT",
      `/api/clients/${clientId}/tov/${path}/${id}`,
      TovVersionResponseSchema,
      input,
    ),
  deleteDefinition: (clientId: string, path: string, id: string) =>
    request(
      "DELETE",
      `/api/clients/${clientId}/tov/${path}/${id}`,
      TovVersionResponseSchema,
    ),
  listSources: (clientId: string, versionId: string) =>
    request(
      "GET",
      `/api/clients/${clientId}/tov/versions/${versionId}/sources`,
      TovSourceListResponseSchema,
    ),
  chunks: (clientId: string, sourceId: string) =>
    request(
      "GET",
      `/api/clients/${clientId}/tov/sources/${sourceId}/chunks`,
      TovChunkListResponseSchema,
    ),
  // Re-fires the parse->vectorize->map cascade; the backend resets a failed
  // source to parse_requested, so this is the recovery path for failed uploads.
  retryParse: (clientId: string, sourceId: string) =>
    request(
      "POST",
      `/api/clients/${clientId}/tov/sources/${sourceId}/parse`,
      TovSourceResponseSchema,
    ),
  parsedPages: (clientId: string, sourceId: string) =>
    request(
      "GET",
      `/api/clients/${clientId}/tov/sources/${sourceId}/parsed-pages`,
      TovParsedPageListResponseSchema,
    ),
  uploadSource: (
    clientId: string,
    versionId: string,
    file: File,
    meta: TovSourceUploadMeta,
  ) => {
    const form = new FormData();
    form.set("file", file);
    form.set("sourceType", meta.sourceType);
    form.set("title", meta.title);
    return uploadRequest(
      `/api/clients/${clientId}/tov/versions/${versionId}/sources/upload`,
      form,
      TovSourceResponseSchema,
    );
  },
  createVersion: (
    clientId: string,
    profileId: string,
    input: TovVersionCreateRequest,
  ) =>
    request(
      "POST",
      `/api/clients/${clientId}/tov/profiles/${profileId}/versions`,
      TovVersionResponseSchema,
      input,
    ),
  addTrait: (
    clientId: string,
    versionId: string,
    input: TovTraitCreateRequest,
  ) =>
    request(
      "POST",
      `/api/clients/${clientId}/tov/versions/${versionId}/traits`,
      TovVersionResponseSchema,
      input,
    ),
  activateVersion: (clientId: string, versionId: string) =>
    request(
      "POST",
      `/api/clients/${clientId}/tov/versions/${versionId}/activate`,
      TovVersionResponseSchema,
    ),
  latestMapAttempt: (clientId: string, versionId: string) =>
    request(
      "GET",
      `/api/clients/${clientId}/tov/versions/${versionId}/map-attempts/latest`,
      TovMapAttemptSummaryResponseSchema,
    ),
  mapVersion: (clientId: string, versionId: string) =>
    request(
      "POST",
      `/api/clients/${clientId}/tov/versions/${versionId}/map`,
      TovMapEnqueueResponseSchema,
    ),
};
